/**
 * Model Detail Page
 *
 * Displays model information, evaluation metrics, and training job link.
 */

import { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Textarea,
} from '@heroui/react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Archive, ArrowLeft, Edit2, ExternalLink, Save, X } from 'lucide-react';

import { useModel, useUpdateModel, useArchiveModel } from '@/lib/api/hooks/models';
import { formatRelativeTime, formatBytes } from '@/lib/design-system';

export const Route = createFileRoute('/models/$modelId')({
  component: ModelDetailPage,
});

function ModelDetailPage() {
  const { modelId } = Route.useParams();
  const navigate = useNavigate();
  const { data: model, isLoading, error } = useModel(modelId);
  const updateModel = useUpdateModel(modelId);
  const archiveModel = useArchiveModel();

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);

  const handleEditStart = () => {
    setEditName(model?.name || '');
    setEditDescription(model?.description || '');
    setIsEditing(true);
  };

  const handleEditSave = async () => {
    try {
      await updateModel.mutateAsync({
        name: editName || null,
        description: editDescription || null,
      });
      setIsEditing(false);
    } catch {
      // Error handling is managed by the mutation
    }
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditName('');
    setEditDescription('');
  };

  const handleArchive = async () => {
    try {
      await archiveModel.mutateAsync(modelId);
      setArchiveModalOpen(false);
      navigate({ to: '/models' });
    } catch {
      // Error handling is managed by the mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="container-page py-6">
        <Card>
          <CardBody className="text-center text-danger">
            Failed to load model. It may have been deleted.
          </CardBody>
        </Card>
      </div>
    );
  }

  const evalMetrics = model.eval_metrics as Record<string, { precision?: number; recall?: number; f1?: number }> | null;

  return (
    <div className="container-page py-6">
      <Button
        variant="light"
        size="sm"
        startContent={<ArrowLeft className="h-4 w-4" />}
        onPress={() => navigate({ to: '/models' })}
        className="mb-4"
      >
        Back to Models
      </Button>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex-1">
          {isEditing ? (
            <div className="space-y-3 max-w-md">
              <Input
                label="Model Name"
                labelPlacement="outside"
                value={editName}
                onValueChange={setEditName}
                placeholder="Enter model name"
              />
              <Textarea
                label="Description"
                labelPlacement="outside"
                value={editDescription}
                onValueChange={setEditDescription}
                placeholder="Enter description"
                minRows={2}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  color="primary"
                  startContent={<Save className="h-4 w-4" />}
                  onPress={handleEditSave}
                  isLoading={updateModel.isPending}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="flat"
                  startContent={<X className="h-4 w-4" />}
                  onPress={handleEditCancel}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <h1>
                  {model.name || `Model ${modelId.slice(0, 8)}`}
                </h1>
                <Chip
                  size="sm"
                  variant="flat"
                  color={model.is_active ? 'success' : 'default'}
                >
                  {model.is_active ? 'Active' : 'Archived'}
                </Chip>
              </div>
              {model.description && (
                <p className="mt-1 text-default-500">{model.description}</p>
              )}
              <p className="mt-2 text-sm text-default-400">
                Created {formatRelativeTime(new Date(model.created_at))}
              </p>
            </>
          )}
        </div>
        {!isEditing && (
          <div className="flex gap-2">
            <Button
              variant="flat"
              size="sm"
              startContent={<Edit2 className="h-4 w-4" />}
              onPress={handleEditStart}
            >
              Edit
            </Button>
            {model.is_active && (
              <Button
                variant="flat"
                color="danger"
                size="sm"
                startContent={<Archive className="h-4 w-4" />}
                onPress={() => setArchiveModalOpen(true)}
              >
                Archive
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Model Info */}
        <Card>
          <CardHeader>
            <h3>Model Information</h3>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-default-500">Model ID</span>
              <span className="font-mono text-sm">{model.id.slice(0, 8)}...</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-default-500">Model Size</span>
              <span>{model.model_size_bytes ? formatBytes(model.model_size_bytes) : '-'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-default-500">Artifact Types</span>
              <div className="flex flex-wrap gap-1 justify-end">
                {model.artifact_types.map((type) => (
                  <Chip key={type} size="sm" variant="flat">
                    {type}
                  </Chip>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-default-500">Training Job</span>
              <Button
                as={Link}
                to={`/training/${model.training_job_id}`}
                size="sm"
                variant="flat"
                endContent={<ExternalLink className="h-3 w-3" />}
              >
                View Job
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Architecture */}
        <Card>
          <CardHeader>
            <h3>Architecture</h3>
          </CardHeader>
          <CardBody className="space-y-4">
            {model.architecture && typeof model.architecture === 'object' ? (
              Object.entries(model.architecture).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-default-500 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span>{String(value)}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-default-500">No architecture information available</p>
            )}
          </CardBody>
        </Card>

        {/* Evaluation Metrics */}
        {evalMetrics && Object.keys(evalMetrics).length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader>
              <h3>Evaluation Metrics</h3>
            </CardHeader>
            <CardBody>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-default-200">
                      <th className="py-2 px-3 text-left font-medium text-default-600">Artifact Type</th>
                      <th className="py-2 px-3 text-right font-medium text-default-600">Precision</th>
                      <th className="py-2 px-3 text-right font-medium text-default-600">Recall</th>
                      <th className="py-2 px-3 text-right font-medium text-default-600">F1 Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(evalMetrics).map(([type, metrics]) => (
                      <tr key={type} className="border-b border-default-100">
                        <td className="py-2 px-3">{type}</td>
                        <td className="py-2 px-3 text-right font-mono">
                          {metrics.precision != null ? (metrics.precision * 100).toFixed(1) + '%' : '-'}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {metrics.recall != null ? (metrics.recall * 100).toFixed(1) + '%' : '-'}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          <span className={
                            metrics.f1 != null && metrics.f1 >= 0.8 ? 'text-success' :
                            metrics.f1 != null && metrics.f1 >= 0.6 ? 'text-warning' :
                            ''
                          }>
                            {metrics.f1 != null ? (metrics.f1 * 100).toFixed(1) + '%' : '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {/* Archive Confirmation Modal */}
      <Modal isOpen={archiveModalOpen} onClose={() => setArchiveModalOpen(false)}>
        <ModalContent>
          <ModalHeader>Archive Model</ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to archive{' '}
              <span className="font-medium">{model.name || `Model ${modelId.slice(0, 8)}`}</span>?
            </p>
            <p className="mt-2 text-sm text-default-500">
              Archived models cannot be used for inference. You can restore them later.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={() => setArchiveModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={handleArchive}
              isLoading={archiveModel.isPending}
            >
              Archive
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
